import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { MaskedLine, Reveal, Eyebrow, EASE } from "@/components/Reveal";
import { getPost, getAdjacent } from "@/data/blogs";

export default function BlogPost() {
    const { slug } = useParams();
    const post = getPost(slug);

    if (!post) {
        return (
            <section data-testid="blog-not-found" className="mx-auto max-w-7xl px-6 pt-48 pb-40 md:px-10">
                <h1 className="font-display text-4xl font-extrabold tracking-tight">Article not found.</h1>
                <Link to="/blog" data-testid="blog-not-found-back" className="mt-8 inline-flex items-center gap-2 text-gold">
                    <ArrowLeft size={16} /> Back to Insights
                </Link>
            </section>
        );
    }

    const { next } = getAdjacent(slug);

    return (
        <>
            <article data-testid="blog-post" className="relative overflow-hidden">
                <div className="pointer-events-none absolute -top-40 right-[-10%] h-[26rem] w-[26rem] rounded-full bg-[#D4AF37]/10 blur-3xl" />
                <header className="mx-auto max-w-4xl px-6 pt-40 md:pt-48">
                    <MaskedLine delay={0.05}>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                            <span className="text-gold">{post.category}</span>
                            <span>{post.date}</span>
                            <span>{post.readTime}</span>
                        </div>
                    </MaskedLine>
                    <h1 className="mt-8 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.04] text-balance">
                        <MaskedLine delay={0.2}>{post.title}</MaskedLine>
                    </h1>
                    <Reveal delay={0.5} className="mt-8">
                        <p className="font-editorial italic font-light text-xl md:text-2xl leading-relaxed text-zinc-400">
                            {post.excerpt}
                        </p>
                        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                            By the Pocketfolio Research Desk
                        </p>
                    </Reveal>
                </header>

                <div className="mx-auto max-w-5xl px-6">
                    <motion.div
                        initial={{ clipPath: "inset(0% 0% 100% 0%)" }}
                        animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
                        transition={{ duration: 1.1, ease: EASE, delay: 0.55 }}
                        className="mt-14 overflow-hidden border border-white/10"
                    >
                        <img
                            src={post.image}
                            alt={post.title}
                            data-testid="blog-post-image"
                            className="h-[300px] md:h-[440px] w-full object-cover opacity-85"
                        />
                    </motion.div>
                </div>

                <div className="mx-auto max-w-3xl px-6 py-20 md:py-28">
                    {post.content.map((block, i) => (
                        <Reveal key={i} delay={0.05}>
                            <section data-testid={`blog-post-section-${i}`} className="mb-12">
                                {block.h && (
                                    <h2 className="mb-6 font-display text-2xl md:text-3xl font-extrabold tracking-tight text-white">
                                        {block.h}
                                    </h2>
                                )}
                                {block.p.map((para, j) => (
                                    <p key={j} className="mb-6 text-base leading-[1.9] text-zinc-300">
                                        {para}
                                    </p>
                                ))}
                            </section>
                        </Reveal>
                    ))}

                    <Reveal>
                        <div className="mt-8 border border-gold/25 bg-coal/40 p-8 md:p-10">
                            <Eyebrow>Trade this with us</Eyebrow>
                            <p className="mt-4 text-sm leading-relaxed text-zinc-300">
                                Our bots, signals and education programmes are built on the same research you
                                just read. Tell the desk what you are working towards.
                            </p>
                            <Link
                                to="/contact"
                                data-testid="blog-post-cta-contact"
                                className="mt-7 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-medium text-black transition-colors duration-300 hover:bg-gold-light"
                            >
                                Contact the desk <ArrowRight size={15} />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </article>

            <nav data-testid="blog-post-nav" className="border-t border-white/10 bg-coal/30">
                <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-6 px-6 py-14">
                    <Link
                        to="/blog"
                        data-testid="blog-post-back"
                        className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors duration-300"
                    >
                        <ArrowLeft size={15} /> All Insights
                    </Link>
                    {next && (
                        <Link
                            to={`/blog/${next.slug}`}
                            data-testid="blog-post-next"
                            className="group max-w-md text-right"
                        >
                            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                                Next briefing
                            </span>
                            <span className="mt-2 flex items-center justify-end gap-2 font-display text-lg font-extrabold tracking-tight text-white transition-colors duration-300 group-hover:text-gold">
                                {next.title} <ArrowRight size={16} className="shrink-0 text-gold" />
                            </span>
                        </Link>
                    )}
                </div>
            </nav>
        </>
    );
}
